<template>
  <div
    :class="{
      'event-instances': hasValidPricingScheduleId === true,
      'dates-and-locations__content-none': hasValidPricingScheduleId === false
    }">
    <template v-if="hasValidPricingScheduleId">
      <div v-if="loading"
           class="event-instances__loading">
        <LoadingSpinner></LoadingSpinner>
      </div>
      <ul v-else
          class="event-instances__list">
        <li v-for="(instance, index) in validInstances"
            :key="index"
            class="event-instance">
          <div v-if="instance.showLocation"
               class="event-instance__map-wrapper">
            <iframe
              v-if="instance.googleMapUrl"
              title="Event location map"
              class="event-instance__map"
              :src="instance.googleMapUrl"></iframe>
          </div>

          <div class="event-instance__content-wrapper">
            <font-awesome-icon
              class="event-instance__location-icon"
              icon="map-marker-alt"></font-awesome-icon>
            <div class="event-instance__content">
              <div class="event-instance__location">
                {{ instance.location }}
              </div>
              <div :class="instance.classes.eventLocations">
                <div v-if="purchaseAllowed"
                     class="event-instance__price">
                  {{ instance.priceExVat }}

                  <template v-if="shouldShowVat(instance)">
                    + vat
                  </template>
                </div>
                <div v-if="instance.startDate"
                     class="event-instance__date">
                  <span>
                    Start:
                  </span>
                  {{ instance.startDate }}
                </div>
                <div v-if="instance.endDate"
                     class="event-instance__duration">
                  <span>
                    End:
                  </span>
                  {{ instance.endDate }}
                </div>
              </div>
              <!-- eslint-disable vue/no-v-html-->
              <div
                v-if="instance.showLocation"
                class="event-instance__address"
                v-html="instance.address"></div>
              <!-- eslint-enable vue/no-v-html-->
              <div :class="['event-instance__actions', instance.classes.eventActions]">
                <span :id="`book_${instance.id}`">
                  <template v-if="instance.hasBookingAvailability">
                    <span :id="`book_btn_${instance.id}`">
                      <button
                        :disabled="
                          !webAccountId || !instance.hasBookingAvailability || !purchaseAllowed
                        "
                        type="button"
                        class="event-instance__book-now btn btn-accent-6"
                        @click="onClickBook(instance)">
                        <template v-if="instance.hasBookingAvailability">
                          Book now
                        </template>
                        <template v-else>
                          Fully booked
                        </template>
                      </button>
                    </span>
                    <button
                      v-if="enableWishlistEvents"
                      type="button"
                      :disabled="!webAccountId || !instance.spacesAvailable"
                      class="event-instance__book-now btn btn-accent-6"
                      @click="onWishlistClick(instance)">
                      Add to wish list
                    </button>
                  </template>
                  <template v-else>
                    <span
                      :id="instance.hasWaitingListSpaces ? '' : 'disabled-wrapper'"
                      tabindex="0">
                      <button
                        :class="[
                          'event-instance__book-now',
                          'btn',
                          'btn-accent-6',
                          instance.classes.eventBookNow
                        ]"
                        :disabled="!webAccountId || !instance.hasWaitingListSpaces"
                        @click="onClickJoin(instance)">
                        Join waiting list
                      </button>
                    </span>
                    <b-tooltip target="disabled-wrapper"
                               variant="light">
                      Sorry, the waiting list is full for this event.
                    </b-tooltip>
                  </template>
                </span>

                <button
                  v-if="instance.sessions && instance.sessions.length > 1"
                  class="event-instance__view-sessions btn btn-accent-6 btn-rounded"
                  @click="onClickSessions(instance)">
                  View sessions
                </button>

                <div
                  v-if="instance.hasLimitedSpaces && instance.spacesAvailable"
                  class="event-instance__remaining-spaces">
                  Only {{ instance.spacesAvailable }}
                  {{ instance.spacesAvailable > 1 ? "places" : "place" }}
                  remaining
                </div>
              </div>

              <b-tooltip
                v-if="userMessage && !purchaseAllowed"
                variant="light"
                custom-class="event-instance__tooltip"
                :target="`book_btn_${instance.id}`">
                <p>
                  {{ userMessage }}
                </p>
              </b-tooltip>

              <b-tooltip
                v-if="purchaseAllowed && !webAccountId"
                :target="`book_${instance.id}`"
                variant="light"
                custom-class="event-instance__tooltip"
                title="Log in to book">
                <p>Log in to book</p>
                <a class="btn btn-accent-6 btn-sm"
                   :href="loginUrl">
                  Log in
                </a>
              </b-tooltip>
            </div>
          </div>
        </li>
        <li v-if="validInstances.length <= 0">
          <div class="dates-and-locations__content-none">
            There are currently no dates available.
            <a
              href="https://newzapp.co.uk/clients/forms/dcc/1514/marketing.html"
              rel="noopener noreferrer ">
              Sign up to receive regular updates about new courses, conferences, education services
              and resources.
            </a>
          </div>
        </li>
      </ul>

      <ErrorModal
        :show="errorModal.show"
        :heading="errorModal.heading"
        :title="errorModal.title"
        :error-message="errorModal.message">
      </ErrorModal>

      <EventBookingModal
        :show="showBookingModal"
        :event-title="eventTitle"
        :instance="selectedInstance"
        :pricing-schedule-id="pricingScheduleId"
        :web-account-id="webAccountId"
        :wishlist-id="wishlistId"
        :booking-from-wishlist="bookingFromWishlist"
        :relationship-type-ids="relationshipTypeIds"
        @close="onCloseBookingModal"
        @join="onBookingJoin"></EventBookingModal>

      <EventWaitingListModal
        :show="showWaitingListModal"
        :event-title="eventTitle"
        :instance="selectedInstance"
        :web-account-id="webAccountId"
        :wanted-quantity="wantedQuantity"
        @error="handleWaitingListError"
        @close="onCloseWaitingListModal"></EventWaitingListModal>

      <EventSessionsModal
        :show="showSessionsModal"
        :event-title="eventTitle"
        :instance="selectedInstance"
        @close="onCloseSessionsModal"></EventSessionsModal>

      <WishlistModal
        v-if="enableWishlistEvents"
        :show="showWishlistModal"
        :show-login-prompt="false"
        @requestSubmit="onWishlistSubmit"
        @close="() => (showWishlistModal = false)"></WishlistModal>
    </template>
    <template v-else>
      There are currently no dates available.
      <a
        href="https://newzapp.co.uk/clients/forms/dcc/1514/marketing.html"
        rel="noopener noreferrer ">
        Sign up to receive regular updates about new courses, conferences, education services and
        resources.
      </a>
    </template>
  </div>
</template>

<script>
import ErrorModal from "~/Components/Common/ErrorModal.vue";
import EventBookingModal from "~/Components/Events/EventBookingModal.vue";
import EventWaitingListModal from "~/Components/Events/EventWaitingListModal.vue";
import EventSessionsModal from "~/Components/Events/EventSessionsModal.vue";
import LoadingSpinner from "~/Components/LoadingSpinner.vue";
import WishlistModal from "~/Components/Wishlist/WishlistModal.vue";
import { listInstances } from "~/Scripts/resources/eventsResource";
import { calcBookingAvailability, calcAvailabilityPercentage } from "~/Scripts/modules/events";
import { BrowserMixin } from "~/Scripts/mixins/common";

// #region types
/**
 * @typedef {Object} Address
 * @property {string} addressId UUID
 * @property {string} orgId UUID
 * @property {string} orgName
 * @property {string} line1
 * @property {string} line2
 * @property {string} line3
 * @property {string} town
 * @property {string} region
 * @property {string} postcode
 * @property {number} [countryId]
 * @property {string} country
 */

/**
 * @typedef {Object} Session
 * @property {string} name
 * @property {string} description
 * @property {string} endDateTime Date is already a formatted string
 * @property {string} startDateTime Date is already a formatted string
 * @property {Address} address
 * @property {boolean} limitNumberOfSpaces
 * @property {boolean} [mandatory]
 * @property {number} [maxSpaces]
 * @property {number} [spacesAvailable]
 * @property {number} [spacesBooked]
 */

/**
 * @typedef {Object} EventInstance
 * @property {string} id UUID
 * @property {string} eventId UUID
 * @property {string} [defaultSessionId] UUID
 * @property {number} [productId]
 * @property {boolean} showLocation
 * @property {boolean} showDisabilityOptions
 * @property {boolean} showDietaryRequirements
 * @property {string} location
 * @property {string} duration
 * @property {string} startDate Date is already a formatted string
 * @property {string} endDate Date is already a formatted string
 * @property {string} address
 * @property {string} postcode
 * @property {string} priceExVat
 * @property {number} [vatRate] double
 * @property {boolean} applyVat
 * @property {number} productId
 * @property {number} [spacesAvailable]
 * @property {number} [minDelegates]
 * @property {number} [maxDelegates]
 * @property {number} [maxDelegatesPerOrganisation]
 * @property {number} [maxDelegatesOnWaitingList]
 * @property {Array<Session>} sessions
 * @property {string} instanceType
 */
// #endregion types

export default {
  components: {
    ErrorModal,
    EventBookingModal,
    EventWaitingListModal,
    EventSessionsModal,
    WishlistModal,
    LoadingSpinner
  },
  mixins: [BrowserMixin],
  props: {
    eventId: {
      type: String,
      required: true
    },
    eventTitle: {
      type: String,
      default: null
    },
    webAccountId: {
      type: String,
      default: null
    },
    pricingScheduleId: {
      type: Number,
      default: null
    },
    purchaseAllowed: {
      type: Boolean,
      default: false
    },
    enableWishlistEvents: {
      type: Boolean,
      default: false
    },
    userMessage: {
      type: String,
      default: "User message here"
    },
    hasValidPricingSchedule: {
      type: Boolean,
      default: false
    },
    lowStockPercentageCap: {
      type: Number,
      default: 10
    },
    lowStockCountCap: {
      type: Number,
      default: 3
    },
    mapApiKey: {
      type: String,
      required: true
    },
    delegateBookingViewModel: {
      type: Object,
      default: () => ( {} )
    },
    relationshipTypeIds: {
      type: Array,
      default: () => []
    }
  },
  provide () {
    return {
      delegateBookingViewModel: this.delegateBookingViewModel
    };
  },
  data: () => ( {
    /** @type {EventInstance} */
    selectedInstance: null,
    showBookingModal: false,
    bookingFromWishlist: false,
    showWaitingListModal: false,
    showWishlistModal: false,
    wishlistId: null,
    showSessionsModal: false,
    /** @type {Array<EventInstance>} */
    instances: [],
    wantedQuantity: null,
    loading: true,
    hasValidPricingScheduleId: false,
    errorModal: {
      show: false,
      heading: undefined,
      title: undefined,
      message: undefined
    },
    debug: false
  } ),
  computed: {
    loginUrl () {
      let result = "/login";

      result += `?returnUrl=${window.location.pathname}`;

      return result;
    },
    validInstances () {
      return this.instances.map( instance => {
        const {
          id,
          location,
          spacesAvailable,
          maxDelegates,
          postcode,
          showLocation,
          waitingListSpacesAvailable
        } = instance;

        const hasBookingAvailability = this.spacesAvailable( instance );
        const availabilityPercentage = calcAvailabilityPercentage( instance );
        // eslint-disable-next-line
        const hasLimitedSpaces = ( availabilityPercentage > 0 && availabilityPercentage <= this.lowStockPercentageCap )
          || ( spacesAvailable > 0 && spacesAvailable <= this.lowStockCountCap );

        const googleMapUrl = postcode ? this.mapUrl( postcode ) : null;

        const hasWaitingListSpaces = waitingListSpacesAvailable > 0;

        if ( this.debug ) {
          console.table( {
            ID: id,
            Show_Location: showLocation,
            Location: location,
            Spaces_Available: spacesAvailable,
            Max_Delegates: maxDelegates,
            Availability: `${availabilityPercentage}%`,
            Low_Stock_Percentage_Cap: `${this.lowStockPercentageCap}%`,
            Low_Stock_Count_Cap: this.lowStockCountCap,
            API_KEY: this.mapApiKey,
            MAP_URL: googleMapUrl,
            Postcode: postcode,
            Waiting_List_Spaces_Available: waitingListSpacesAvailable,
            Has_Waiting_List_Spaces: hasWaitingListSpaces
          } );
        }

        const classes = {
          eventActions: { "event-instance-landscape__actions": !instance.showLocation },
          eventBookNow: { "event-instance__book-now--disabled": !instance.hasWaitingListSpaces },
          eventLocation: { "event-instance__content-landscape": !instance.showLocation }
        };

        return {
          ...instance,
          hasBookingAvailability,
          hasLimitedSpaces,
          googleMapUrl,
          hasWaitingListSpaces,
          classes
        };
      } );
    }
  },
  watch: {
    pricingScheduleId: {
      immediate: true,
      handler ( val ) {
        if ( val !== null ) {
          this.hasValidPricingScheduleId = true;
          listInstances( this.eventId, val )
            .then( response => {
              const { data } = response || { data: [] };
              this.instances = data;
              this.emitInstanceConfig();
            } )
            .catch( console.error )
            .finally( () => {
              this.loading = false;
            } );
        } else {
          this.hasValidPricingSchedule = false;
          this.loading = false;
        }
      }
    }
  },
  created () {
    this.debug = this.getUrlParameter( "debug" );
  },
  methods: {
    /** @type {Function}
     * @param {EventInstance} instance
     * @returns {boolean}
     */
    spacesAvailable ( instance ) {
      const result = calcBookingAvailability( instance ) !== 0;
      return result;
    },
    /**
     * If there are sessions, return first session's start time, otherwise returns event start time
     * @param {EventInstance} instance
     * @returns {string|*}
     */
    getInstanceStartDate ( instance ) {
      if ( instance.sessions.length > 0 ) {
        if ( instance.sessions[0].startDateTime ) {
          return instance.sessions[0].startDateTime;
        }
      }
      return instance.startDate;
    },
    shouldShowVat ( instance ) {
      let result = false;

      if ( instance.vatRate > 0 ) {
        result = true;
      }

      return result;
    },
    onWishlistSubmit ( args ) {
      this.wishlistId = args.data.id;
      this.showWishlistModal = false;
      this.bookingFromWishlist = true;
      this.showBookingModal = true;
    },
    onWishlistClick ( instance ) {
      this.showWishlistModal = true;
      this.selectedInstance = instance;
    },
    onClickBook ( instance ) {
      if ( this.spacesAvailable( instance ) ) {
        this.selectedInstance = instance;
        this.bookingFromWishlist = false;
        this.showBookingModal = true;
      }
    },
    onClickJoin ( instance ) {
      this.selectedInstance = instance;
      this.showWaitingListModal = true;
    },
    onBookingJoin ( instanceId, wantedQuantity ) {
      const match = this.instances.find( x => x.id === instanceId );
      this.wantedQuantity = wantedQuantity;

      if ( match ) {
        this.selectedInstance = match;
        this.showWaitingListModal = true;
      }
    },
    onClickSessions ( instance ) {
      this.selectedInstance = instance;
      this.showSessionsModal = true;
    },
    onCloseBookingModal () {
      this.selectedInstance = null;
      this.showBookingModal = false;
      this.bookingFromWishlist = false;
      this.wishlistId = null;
    },
    onCloseWaitingListModal () {
      this.selectedInstance = null;
      this.showWaitingListModal = false;
    },
    onCloseSessionsModal () {
      this.selectedInstance = null;
      this.showSessionsModal = false;
    },
    handleWaitingListError ( apiErrorCode ) {
      this.errorModal.show = true;
      this.errorModal.heading = "Event booking";

      switch ( apiErrorCode ) {
        case 108:
        case "108":
          this.errorModal.title = "Attendance capacity reached";
          this.errorModal.message = `<p>You have reached the maximum number of delegates permitted from one organisation</p>
                                     <p>
                                     <span class="event-booking-modal__asterisk">*</span>
                                     <i>This will include delegates from other bookings</i>
                                     </p>`;
          break;
        case 120:
        case "120":
          this.errorModal.title = "No spaces available";
          this.errorModal.message = "<p>There are not enough spaces availble to complete your booking.</p>";
          break;
        case 117:
        case "117":
          this.errorModal.title = "Waiting list is full";
          this.errorModal.message = "<p>We are sorry, due to popularity the waiting list for this event is full.</p>";
          break;
        default:
          break;
      }
    },
    /**
     * Get the Google Maps URL based on the post code
     *
     * @param {string} postcode The session post code
     */
    mapUrl ( postcode ) {
      return `https://www.google.com/maps/embed/v1/place?key=${this.mapApiKey}&q=${postcode}`;
    },

    /**
     * Get varous bits of configuration from the list of instances and emit it to the parent
     */
    emitInstanceConfig () {
      let config = null;

      if ( this.instances?.length > 0 ) {
        const hasLocation = !!this.instances.find( instance => instance.location?.length > 0 );
        const { instanceType } = this.instances[0];

        config = {
          hasLocation,
          instanceType
        };
      } else {
        config = {
          hasLocation: false,
          instanceType: null
        };
      }

      this.$emit( "instance-config-changed", config );
    }
  }
};
</script>

<style scoped lang="less">
.event-instance {
  &-landscape {
    &__actions {
      button {
        width: auto;
      }
    }
  }

  &__remaining-spaces {
    color: #D60000;
    font-weight: 600;
    margin: 8px 0 0;
    font-size: 0.875rem;
  }

  &__content {
    &-landscape {
      display: flex;
      flex-wrap: wrap;

      div {
        font-size: 1em;

        &:last-child {
          &:after {
            display: none;
          }
        }

        &:after {
          content: "";
          background-color: #000;
          position: relative;
          display: inline-flex;
          width: 1.5px;
          height: 16px;
          margin: 0 12px;
          padding: 2px 0;
        }
      }
    }
  }
}
</style>
